import React from "react";
import MegaHeader from "../MegaHeader";
import { imageUrl } from "../../utils/imagePath";
import "./CompetitiveLandscapeMonitoringPlatform.scss";

const IMG = (file) =>
  imageUrl("competitive-landscape-monitoring-platform", file);

const EMBED_URL = "https://pharma-scope.lovable.app";

const CompetitiveLandscapeMonitoringPlatform = () => {
  return (
    <>
      <MegaHeader />

      {/* Hero */}
      <section
        className="clmp-hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.61) 0%, rgba(0, 0, 0, 0.55) 50%, rgba(0, 0, 0, 0.61) 100%), url(${imageUrl("competitive-landscape-monitoring-platform", "landscape.svg")})`,
        }}
      >
        <div className="clmp-hero__content">
          <h1 className="clmp-hero__title">
            Competitive Landscape Monitoring Platform
          </h1>
          <p className="clmp-hero__subtitle">
            An AI-powered intelligence platform that gives pharmaceutical and
            biotech teams a real-time edge on competitor activity and industry
            developments.
          </p>
        </div>
      </section>

      {/* The Challenge */}
      <section className="clmp-section clmp-challenge">
        <h2 className="clmp-section__title">The Challenge</h2>
        <p className="clmp-section__intro">
          Pharmaceutical and biotech companies operate in a fast-moving
          landscape where clinical trial updates, regulatory decisions, and
          competitor pipeline changes can reshape strategy overnight.
        </p>
        <div className="clmp-challenge-grid">
          <div className="clmp-challenge-card">
            <div className="clmp-challenge-card__icon-wrap">
              <img src={IMG("search.svg")} alt="" className="clmp-challenge-card__icon" />
            </div>
            <h3 className="clmp-challenge-card__title">Scattered Data Sources</h3>
            <p className="clmp-challenge-card__text">
              Competitive intelligence data is scattered across fragmented public
              and private sources.
            </p>
          </div>
          <div className="clmp-challenge-card">
            <div className="clmp-challenge-card__icon-wrap">
              <img src={IMG("manual-search.svg")} alt="" className="clmp-challenge-card__icon" />
            </div>
            <h3 className="clmp-challenge-card__title">Manual Research Time</h3>
            <p className="clmp-challenge-card__text">
              Analysts sift through lengthy reports and announcements, wasting
              valuable time and delaying action.
            </p>
          </div>
          <div className="clmp-challenge-card">
            <div className="clmp-challenge-card__icon-wrap">
              <img src={IMG("missed-opportunites.svg")} alt="" className="clmp-challenge-card__icon" />
            </div>
            <h3 className="clmp-challenge-card__title">Missed Opportunities</h3>
            <p className="clmp-challenge-card__text">
              Critical developments may be missed due to information overload
              and slow manual monitoring.
            </p>
          </div>
          <div className="clmp-challenge-card">
            <div className="clmp-challenge-card__icon-wrap">
              <img src={IMG("limited-visibility.svg")} alt="" className="clmp-challenge-card__icon" />
            </div>
            <h3 className="clmp-challenge-card__title">Limited Visibility</h3>
            <p className="clmp-challenge-card__text">
              Teams lack real-time visibility into competitor activity and
              clinical progress, stalling decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="clmp-section clmp-solution">
        <h2 className="clmp-section__title">Solution</h2>
        <p className="clmp-section__intro">
          These challenges were addressed by providing an integrated platform
          that aggregates industry data and transforms it into actionable
          insights through AI-driven analysis and intelligent dashboards. The
          platform enables teams to monitor competitive activity in real time
          while reducing the manual effort required to track market
          developments.
        </p>
        <div className="clmp-solution-link-row">
          <a
            href={EMBED_URL}
            target="_blank"
            rel="noreferrer"
            className="clmp-demo-link"
            aria-label="View demo in a new tab"
          >
            <span className="clmp-demo-link__label">View Demo</span>
            <img
              src={IMG("link.svg")}
              className="clmp-demo-link__icon"
              alt=""
              aria-hidden="true"
            />
          </a>
        </div>
        <div className="clmp-solution-embed-wrapper">
          <iframe
            src={EMBED_URL}
            title="Competitive Landscape Monitoring"
            className="clmp-iframe"
            allowFullScreen
            scrolling="yes"
          />
        </div>
      </section>

      {/* Key Features */}
      <section className="clmp-section clmp-features">
        <h2 className="clmp-section__title">Key Features</h2>
        <div className="clmp-features-grid">
          <div className="clmp-feature-card">
            <div className="clmp-feature-card__icon-wrap" style={{ background: 'linear-gradient(135deg, #F0B100 0%, #FF6900 100%)' }}>
              <img src={IMG("highlights.svg")} alt="" className="clmp-feature-card__icon" />
            </div>
            <h3 className="clmp-feature-card__title">Highlights</h3>
            <p className="clmp-feature-card__text">
              Quick overview of all the most important competitive insights and
              industry developments.
            </p>
          </div>
          <div className="clmp-feature-card">
            <div className="clmp-feature-card__icon-wrap" style={{ background: 'linear-gradient(135deg, #2B7FFF 0%, #00B8DB 100%)' }}>
              <img src={IMG("pipeline-tracker.svg")} alt="" className="clmp-feature-card__icon" />
            </div>
            <h3 className="clmp-feature-card__title">Pipeline Tracker</h3>
            <p className="clmp-feature-card__text">
              Tracks competitor assets and pipeline activity across therapeutic
              areas.
            </p>
          </div>
          <div className="clmp-feature-card">
            <div className="clmp-feature-card__icon-wrap" style={{ background: 'linear-gradient(135deg, #AD46FF 0%, #F6339A 100%)' }}>
              <img src={IMG("clinical-trial-update.svg")} alt="" className="clmp-feature-card__icon" />
            </div>
            <h3 className="clmp-feature-card__title">Clinical Trial Updates</h3>
            <p className="clmp-feature-card__text">
              Monitors trial progress and updates from ClinicalTrials.gov.
            </p>
          </div>
          <div className="clmp-feature-card">
            <div className="clmp-feature-card__icon-wrap" style={{ background: 'linear-gradient(135deg, #615FFF 0%, #2B7FFF 100%)' }}>
              <img src={IMG("weekly-snapshot.svg")} alt="" className="clmp-feature-card__icon" />
            </div>
            <h3 className="clmp-feature-card__title">Weekly Snapshots</h3>
            <p className="clmp-feature-card__text">
              Weekly summaries highlighting trends, sentiment, and emerging
              developments.
            </p>
          </div>
          <div className="clmp-feature-card">
            <div className="clmp-feature-card__icon-wrap" style={{ background: 'linear-gradient(135deg, #FF6900 0%, #FB2C36 100%)' }}>
              <img src={IMG("data-snacks.svg")} alt="" className="clmp-feature-card__icon" />
            </div>
            <h3 className="clmp-feature-card__title">Data Snacks</h3>
            <p className="clmp-feature-card__text">
              Short, daily insights that capture immediate competitor activity.
            </p>
          </div>
          <div className="clmp-feature-card">
            <div className="clmp-feature-card__icon-wrap" style={{ background: 'linear-gradient(135deg, #00C950 0%, #00BC7D 100%)' }}>
              <img src={IMG("ai-chatbot.svg")} alt="" className="clmp-feature-card__icon" />
            </div>
            <h3 className="clmp-feature-card__title">AI Chatbot</h3>
            <p className="clmp-feature-card__text">
              Natural language assistant that answers questions about
              competitive developments.
            </p>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section
        className="clmp-impact"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.61) 0%, rgba(0, 0, 0, 0.55) 50%, rgba(0, 0, 0, 0.61) 100%),  url(${imageUrl("competitive-landscape-monitoring-platform", "impact.svg")})`,
        }}
      >
        <div className="clmp-impact__content">
          <h2 className="clmp-impact__title">Impact</h2>
          <ul className="clmp-impact__list">
            <li>Faster access to competitive intelligence</li>
            <li>Reduced manual research and analysis</li>
            <li>Improved visibility into competitor pipelines</li>
            <li>Better strategic decision-making for pharmaceutical teams</li>
          </ul>
        </div>
      </section>
    </>
  );
};

export default CompetitiveLandscapeMonitoringPlatform;
